import {
  getSectionSettings,
  SECTION_WORKFLOW_LABELS,
  type AccessibilitySectionId,
  type ShortcutAction,
  type WorkflowStepId,
} from "@/lib/accessibility-settings"

export function labelsToStepIds(labels: string[], sectionId: AccessibilitySectionId): WorkflowStepId[] {
  const labelMap = SECTION_WORKFLOW_LABELS[sectionId]
  const reverseMap = Object.fromEntries(
    Object.entries(labelMap).map(([id, label]) => [label, id]),
  ) as Record<string, WorkflowStepId>

  return labels
    .map((label) => reverseMap[label])
    .filter((id): id is WorkflowStepId => Boolean(id))
}

export function getWorkflowOrder(sectionId: AccessibilitySectionId): WorkflowStepId[] {
  const { workflowOrder } = getSectionSettings(sectionId)
  return labelsToStepIds(workflowOrder, sectionId)
}

function getStepElement(stepId: WorkflowStepId): HTMLElement | null {
  return document.querySelector(`[data-workflow-step="${stepId}"]`) as HTMLElement | null
}

function isElementDisabled(el: HTMLElement): boolean {
  if (el.hasAttribute("disabled")) return true
  if (el.getAttribute("aria-disabled") === "true") return true
  if (el instanceof HTMLButtonElement && el.disabled) return true
  if (el instanceof HTMLInputElement && el.disabled) return true
  if (el instanceof HTMLSelectElement && el.disabled) return true
  if (el instanceof HTMLTextAreaElement && el.disabled) return true
  return false
}

function isStepFocusable(stepId: WorkflowStepId, skipDisabled: boolean): boolean {
  const el = getStepElement(stepId)
  if (!el) return false
  if (skipDisabled && isElementDisabled(el)) return false
  return true
}

export function focusWorkflowStep(stepId: WorkflowStepId): boolean {
  // Legacy "cart" step focuses the first line quantity field when present.
  const resolved: WorkflowStepId = stepId === "cart" ? "quantity" : stepId
  const el = getStepElement(resolved)
  if (!el || isElementDisabled(el)) return false

  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    el.focus()
    if (el instanceof HTMLInputElement && el.type !== "checkbox") el.select()
    return true
  }

  if (!el.hasAttribute("tabindex")) el.tabIndex = -1
  el.focus()
  return true
}

export function getFocusedWorkflowStep(): WorkflowStepId | null {
  const active = document.activeElement
  if (!active) return null
  const host = active.closest("[data-workflow-step]")
  const step = host?.getAttribute("data-workflow-step")
  return step as WorkflowStepId | null
}

export function focusNextWorkflowStep(sectionId: AccessibilitySectionId, direction: 1 | -1 = 1): boolean {
  const { keyboardNav } = getSectionSettings(sectionId)
  const order = getWorkflowOrder(sectionId)
  if (order.length === 0) return false

  const current = getFocusedWorkflowStep()
  // Only move along the configured workflow when already on a workflow step.
  // Otherwise leave native Tab/Enter behavior alone.
  if (!current) return false

  const startIdx = order.indexOf(current)
  if (startIdx < 0) return false

  for (let i = startIdx + direction; direction === 1 ? i < order.length : i >= 0; i += direction) {
    const step = order[i]!
    if (keyboardNav.skipOptional && step === "customer") continue
    if (!isStepFocusable(step, keyboardNav.skipDisabled)) continue
    return focusWorkflowStep(step)
  }

  return false
}

export function shouldAutoFocusBarcode(sectionId: AccessibilitySectionId): boolean {
  return getSectionSettings(sectionId).keyboardNav.autoFocusBarcode
}

export function shouldAutoFocusAfterSale(sectionId: AccessibilitySectionId): boolean {
  return getSectionSettings(sectionId).keyboardNav.autoFocusAfterSale
}

export function matchesShortcut(e: KeyboardEvent, shortcut: string): boolean {
  const parts = shortcut.split("+").map((part) => part.trim())
  const keyPart = parts[parts.length - 1] ?? ""

  const wantCtrl = parts.some((part) => part.toLowerCase() === "ctrl")
  const wantAlt = parts.some((part) => part.toLowerCase() === "alt")
  const wantShift = parts.some((part) => part.toLowerCase() === "shift")

  if (e.ctrlKey !== wantCtrl) return false
  if (e.altKey !== wantAlt) return false
  if (e.shiftKey !== wantShift) return false

  const key = keyPart.toLowerCase()
  if (key === "enter") return e.key === "Enter"
  if (key === "esc" || key === "escape") return e.key === "Escape"
  if (/^f\d+$/i.test(keyPart)) return e.key.toLowerCase() === keyPart.toLowerCase()
  return e.key.toLowerCase() === key
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true
  return target.isContentEditable
}

export function isComboboxExpanded(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const combobox = target.closest('[role="combobox"]')
  return combobox?.getAttribute("aria-expanded") === "true"
}

export { isBarcodeInput } from "@/lib/barcode-scan"

export function getShortcutAction(e: KeyboardEvent, sectionId: AccessibilitySectionId): ShortcutAction | null {
  const { shortcuts } = getSectionSettings(sectionId)
  // Prefer Save/Complete over Focus Barcode if both somehow share a key.
  const ordered = Object.entries(shortcuts).sort(([a], [b]) => {
    if (a === "Complete Sale") return -1
    if (b === "Complete Sale") return 1
    return 0
  }) as [ShortcutAction, string][]
  for (const [action, shortcut] of ordered) {
    if (matchesShortcut(e, shortcut)) return action
  }
  return null
}
